import { loadPublicConfig } from './config';
import { contactCaptureActionUrl, contactCaptureConfigUrl } from './endpoints';
import { emitWidgetEvent } from './events';
import { renderContactCapture, renderLoadError } from './renderer';
import type { EmbeddableType, WidgetSource } from './types';

const INITIALIZED_ATTRIBUTE = 'data-bhi-initialized';

export const mountContactCapture = async (source: WidgetSource) => {
  if (source.host.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
  source.host.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  try {
    const config = await loadPublicConfig(source.configUrl, source.captureId);
    if (source.mode === 'popup' && config.settings.behavior.popupDelaySeconds > 0) {
      emitWidgetEvent('bhi:capture:scheduled', source.captureId, {
        mode: source.mode,
        delaySeconds: config.settings.behavior.popupDelaySeconds,
      });
      await new Promise(resolve => window.setTimeout(
        resolve,
        config.settings.behavior.popupDelaySeconds * 1000,
      ));
    }
    if (source.host.isConnected) renderContactCapture(source, config);
  } catch (error) {
    renderLoadError(source, error);
    emitWidgetEvent('bhi:capture:error', source.captureId, {
      stage: 'config',
      message: error instanceof Error ? error.message : 'Unknown config error',
    });
  }
};

const sourceFromElement = (
  element: HTMLElement,
  mode: EmbeddableType,
  host: HTMLElement,
): WidgetSource | null => {
  const captureId = element.dataset.contactCaptureId || element.dataset.contactCapture;
  if (!captureId) return null;
  // The config URL is resolved from the runtime origin; the attribute only overrides it.
  const configUrl = element.dataset.contactCaptureConfig || contactCaptureConfigUrl(captureId);
  if (element.dataset.contactCapturePreview === 'true') {
    host.setAttribute('data-bhi-preview', 'true');
  }
  return {
    captureId,
    configUrl,
    mode,
    host,
    submissionUrl: element.dataset.contactCaptureSubmit || contactCaptureActionUrl(configUrl, 'submission')?.href,
    previewState: element.dataset.contactCapturePreviewState === 'success' ? 'success' : undefined,
  };
};

const mountPopupScript = (script: HTMLScriptElement) => {
  if (script.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
  const host = document.createElement('div');
  const source = sourceFromElement(script, 'popup', host);
  if (!source) return;
  script.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  document.body.appendChild(host);
  void mountContactCapture(source);
};

export const scanContactCaptures = (currentScript?: HTMLScriptElement | null) => {
  if (currentScript?.dataset.contactCaptureId) mountPopupScript(currentScript);

  document.querySelectorAll<HTMLElement>('[data-contact-capture]').forEach(host => {
    const source = sourceFromElement(host, 'inline', host);
    if (source) void mountContactCapture(source);
  });
};
